/* ============================================================================
   Admin module — Audit log panel. Compact table of recent audit entries with an
   action filter, wired to /api/manage/audit. Detail JSON is summarized inline.
   ============================================================================ */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiGet, isApiError } from '../../lib/api';
import type { AuditEntry } from './types';

function summarize(detailJson: string | null): string {
  if (!detailJson) return '';
  try {
    const d = JSON.parse(detailJson) as Record<string, unknown>;
    if (d.changed && Array.isArray(d.changed)) {
      return (d.changed as { key: string; value: string }[])
        .map((c) => `${c.key} = ${c.value}`)
        .join(', ');
    }
    if (d.from !== undefined || d.to !== undefined) {
      return `${d.name ? `${d.name}: ` : ''}${d.from ?? '—'} → ${d.to ?? '—'}`;
    }
    const parts: string[] = [];
    for (const [k, v] of Object.entries(d)) {
      if (v == null || typeof v === 'object') continue;
      parts.push(`${k}: ${String(v)}`);
    }
    return parts.join(', ');
  } catch {
    return '';
  }
}

export default function AuditPanel() {
  const [rows, setRows] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [action, setAction] = useState('');

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await apiGet<AuditEntry[]>('/api/manage/audit?limit=200');
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(isApiError(e) ? e.message : 'Failed to load audit log.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const actions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.action))).sort(),
    [rows],
  );
  const filtered = useMemo(
    () => (action ? rows.filter((r) => r.action === action) : rows),
    [rows, action],
  );

  if (loading) {
    return (
      <div className="card" style={{ display: 'grid', placeItems: 'center', padding: 48 }}>
        <div className="spinner" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="card empty">
        <h3>Couldn't load the audit log</h3>
        <p className="muted">{error}</p>
      </div>
    );
  }

  return (
    <div className="stack" style={{ gap: 14 }}>
      <div className="row" style={{ gap: 10, alignItems: 'center' }}>
        <select
          value={action}
          onChange={(e) => setAction(e.target.value)}
          style={{ height: 34, maxWidth: 240 }}
        >
          <option value="">All actions</option>
          {actions.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
        <span className="muted">{filtered.length} entries</span>
      </div>

      {filtered.length === 0 ? (
        <div className="card empty">
          <h3>No audit entries</h3>
          <p className="muted">Administrative actions will be recorded here.</p>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="tbl tbl-compact">
            <thead>
              <tr>
                <th>When</th>
                <th>Actor</th>
                <th>Action</th>
                <th>Entity</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id}>
                  <td className="mono" style={{ whiteSpace: 'nowrap' }}>
                    {r.createdAt.slice(0, 19).replace('T', ' ')}
                  </td>
                  <td>{r.actorName ?? <span className="muted">system</span>}</td>
                  <td>
                    <span className="mono">{r.action}</span>
                  </td>
                  <td className="mono muted">
                    {r.entityType}
                    {r.entityId ? `#${r.entityId}` : ''}
                  </td>
                  <td className="muted">{summarize(r.detailJson)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
